/**
 * LockedCapability — the lock-state placeholder shown when an org's
 * plan does not entitle them to a feature.
 *
 * Renders a card that explains the feature, names the entitling
 * plans, and links to the billing surface to upgrade. The mockup at
 * `docs/mockups/admin-billing-plans-and-features.html` doesn't show
 * this surface directly (it's the customer-facing flip side of the
 * admin's per-plan feature toggle); this component is the canonical
 * frontend lock UX for every gated feature going forward.
 *
 * The same component drives every gated capability: Slack, Teams,
 * SSO, audit log export, etc. — the only per-feature variation is
 * the title + description text, supplied as props.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui'
import { Lock } from 'lucide-react'

export interface LockedCapabilityProps {
  /** Display name of the feature ("Slack notifications", "SSO / SAML"). */
  featureName: string
  /** Marketing copy / explanation of the capability. Shown below the
   * title. Markdown-light: plain strings, no formatting. */
  description: string
  /** Plans (display names) that include the feature. Shown in the
   * "Upgrade to" pill list. Pass `[]` to suppress the list (rare). */
  entitlingPlans?: string[]
  /** Optional upgrade-path call-to-action. If absent, the component
   * renders the explainer but no CTA — useful for read-only
   * informational surfaces. */
  onUpgrade?: () => void
  /** Compact mode renders inline within a parent card (no own card
   * shell). Default `false` renders the full Card chrome. */
  compact?: boolean
}

export function LockedCapability({
  featureName,
  description,
  entitlingPlans = [],
  onUpgrade,
  compact = false,
}: LockedCapabilityProps) {
  const body = (
    <div className="flex items-start gap-3">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
        <Lock className="h-4 w-4" />
      </div>
      <div className="flex-1">
        <div className="text-sm font-semibold">{featureName}</div>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>

        {entitlingPlans.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
            <span className="text-muted-foreground">Available on</span>
            {entitlingPlans.map((name) => (
              <span
                key={name}
                className="rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary"
              >
                {name}
              </span>
            ))}
          </div>
        )}

        {onUpgrade && (
          <button
            onClick={onUpgrade}
            type="button"
            className="mt-3 inline-flex items-center rounded border border-primary bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
          >
            See plans
          </button>
        )}
      </div>
    </div>
  )

  if (compact) {
    return (
      <div className="rounded border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-900 dark:bg-amber-950/20">
        {body}
      </div>
    )
  }

  return (
    <Card className="border-amber-200 bg-amber-50/30 dark:border-amber-900 dark:bg-amber-950/10">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Feature locked</CardTitle>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  )
}
